import React, { useEffect, useRef, useState } from "react";
import Button from "../../../components/common/button";
import { useLocation } from "react-router-dom";
import { fetchBankLeadData, updateFirestoreDoc } from "../../../api";
import { doc, getDoc } from "firebase/firestore";
import { db, storage } from "../../../config/firebaseConfig";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../../../components/ui/form";
import toast from "react-hot-toast";
import Ic_file from "../../../assets/images/Ic_file.svg";
import Ic_download_primary from "../../../assets/images/Ic_download_primary.svg";
import {
  getDownloadURL,
  getStorage,
  ref,
  ref as reff,
  uploadBytes,
} from "firebase/storage";
import Ic_trash from "../../../assets/images/Ic_trash.svg";
import FileInfo from "../../../components/FileInfo";
import Modal from "../../../components/common/modal";
import { useAuthData } from "../../../context/AuthContext";
import { Upload } from "lucide-react";

const formSchema = z.object({
  Entreprenørgaranti: z
    .array(
      z.union([
        z
          .instanceof(File)
          .refine(
            (file: any) => file === null || file.size <= 10 * 1024 * 1024,
            {
              message: "Filstørrelsen må være mindre enn 10 MB.",
            }
          ),
        z.string(),
      ])
    )
    .optional(),
  Forsikringsbevis: z
    .array(
      z.union([
        z
          .instanceof(File)
          .refine(
            (file: any) => file === null || file.size <= 10 * 1024 * 1024,
            {
              message: "Filstørrelsen må være mindre enn 10 MB.",
            }
          ),
        z.string(),
      ])
    )
    .optional(),
  Kontrakt: z
    .array(
      z.union([
        z
          .instanceof(File)
          .refine(
            (file: any) => file === null || file.size <= 10 * 1024 * 1024,
            {
              message: "Filstørrelsen må være mindre enn 10 MB.",
            }
          ),
        z.string(),
      ])
    )
    .optional(),
});

export const Documenters: React.FC<{
  setActiveTab: any;
  getData: any;
}> = ({ setActiveTab, getData }) => {
  const form = useForm<any>({
    resolver: zodResolver(formSchema),
  });

  const { adminData } = useAuthData();
  const [Role, setRole] = useState<any>(null);

  useEffect(() => {
    const getData = async () => {
      if (adminData) {
        if (adminData?.role) {
          setRole(adminData?.role);
        }
      }
    };

    getData();
  }, [adminData]);

  const location = useLocation();
  const pathSegments = location.pathname.split("/");
  const id = pathSegments.length > 2 ? pathSegments[2] : null;
  const prevDocumentsRef = useRef({
    Entreprenørgaranti: [],
    Forsikringsbevis: [],
    Kontrakt: [],
  });

  const Entreprenørgaranti: any = form.watch("Entreprenørgaranti") || [];
  const Forsikringsbevis: any = form.watch("Forsikringsbevis") || [];
  const Kontrakt: any = form.watch("Kontrakt") || [];

  useEffect(() => {
    if (!id) {
      return;
    }

    const getData = async () => {
      const data = await fetchBankLeadData(id);
      if (data && data.Documenter) {
        Object.entries(data.Documenter).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            form.setValue(key as any, value);
          }
        });
      }
    };

    getData();
  }, [id]);

  const [isUpload, setIsUpload] = useState(false);

  useEffect(() => {
    const allDocuments = {
      Entreprenørgaranti,
      Forsikringsbevis,
      Kontrakt,
    };

    const currentDocumentsString: any = JSON.stringify(allDocuments);
    const prevDocumentsString: any = prevDocumentsRef.current;

    if (currentDocumentsString !== prevDocumentsString) {
      const hasAnyDocuments =
        Entreprenørgaranti.length > 0 ||
        Forsikringsbevis.length > 0 ||
        Kontrakt.length > 0;

      setIsUpload(hasAnyDocuments);
    }

    prevDocumentsRef.current = currentDocumentsString;
  }, [Entreprenørgaranti, Forsikringsbevis, Kontrakt]);

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    const finalData = {
      ...data,
    };
    if (data.Entreprenørgaranti !== undefined) {
      finalData.Entreprenørgaranti = data.Entreprenørgaranti;
    } else {
      delete finalData.Entreprenørgaranti;
    }
    if (data.Forsikringsbevis !== undefined) {
      finalData.Forsikringsbevis = data.Forsikringsbevis;
    } else {
      delete finalData.Forsikringsbevis;
    }
    if (data.Kontrakt !== undefined) {
      finalData.Kontrakt = data.Kontrakt;
    } else {
      delete finalData.Kontrakt;
    }

    try {
      const docRef = doc(db, "bank_leads", String(id));
      const docSnap = await getDoc(docRef);

      const BankData = {
        ...finalData,
        id: id,
      };
      const formatDate = (date: Date) => {
        return date
          .toLocaleString("sv-SE", { timeZone: "UTC" })
          .replace(",", "");
      };

      await updateFirestoreDoc(db, "bank_leads", String(id), {
        Documenter: BankData,
        updatedAt: formatDate(new Date()),
      });
      toast.success("Lagret", { position: "top-right" });

      if (docSnap.exists() && isUpload && Role === "Agent") {
        const bankLeadData = docSnap.data();

        const response = await fetch(
          "https://nh989m12uk.execute-api.eu-north-1.amazonaws.com/prod/banklead",
          {
            method: "POST",
            headers: {
              Accept: "/",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              action: "document-add",
              firstName: bankLeadData?.Kunden?.Kundeinformasjon[0]?.f_name,
              lastName: bankLeadData?.Kunden?.Kundeinformasjon[0]?.l_name,
              email: bankLeadData?.Kunden?.Kundeinformasjon[0]?.EPost,
              projectAddress:
                bankLeadData?.Kunden?.Kundeinformasjon[0]?.Adresse,
              link: `${process.env.REACT_APP_REDIRECT_URL_ADMIN}/bank-leads-detail/${id}`,
              isTest: Boolean(process.env.REACT_APP_ISTEST),
            }),
          }
        );

        const data = await response.json();
        toast.success(data?.message, { position: "top-right" });
      }

      setActiveTab(3);
      getData();
    } catch (error) {
      console.error("Firestore operation failed:", error);
      toast.error("Something went wrong. Please try again.", {
        position: "top-right",
      });
    }
  };

  const autoSave = async () => {
    const formData = form.getValues();
    const finalData = {
      ...formData,
    };

    if (formData.Entreprenørgaranti !== undefined) {
      finalData.Entreprenørgaranti = formData.Entreprenørgaranti;
    } else {
      delete finalData.Entreprenørgaranti;
    }
    if (formData.Forsikringsbevis !== undefined) {
      finalData.Forsikringsbevis = formData.Forsikringsbevis;
    } else {
      delete finalData.Forsikringsbevis;
    }
    if (formData.Kontrakt !== undefined) {
      finalData.Kontrakt = formData.Kontrakt;
    } else {
      delete finalData.Kontrakt;
    }

    try {
      const docRef = doc(db, "bank_leads", String(id));
      const docSnap = await getDoc(docRef);

      const BankData = {
        ...finalData,
        id: id,
      };
      const formatDate = (date: Date) => {
        return date
          .toLocaleString("sv-SE", { timeZone: "UTC" })
          .replace(",", "");
      };

      await updateFirestoreDoc(db, "bank_leads", String(id), {
        Documenter: BankData,
        updatedAt: formatDate(new Date()),
      });

      if (docSnap.exists() && isUpload && Role === "Agent") {
        const bankLeadData = docSnap.data();

        const response = await fetch(
          "https://nh989m12uk.execute-api.eu-north-1.amazonaws.com/prod/banklead",
          {
            method: "POST",
            headers: {
              Accept: "/",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              action: "document-add",
              firstName: bankLeadData?.Kunden?.Kundeinformasjon[0]?.f_name,
              lastName: bankLeadData?.Kunden?.Kundeinformasjon[0]?.l_name,
              email: bankLeadData?.Kunden?.Kundeinformasjon[0]?.EPost,
              projectAddress:
                bankLeadData?.Kunden?.Kundeinformasjon[0]?.Adresse,
              link: `${process.env.REACT_APP_REDIRECT_URL_ADMIN}/bank-leads-detail/${id}`,
              isTest: Boolean(process.env.REACT_APP_ISTEST),
            }),
          }
        );

        const data = await response.json();
        toast.success(data?.message, { position: "top-right" });
      }

      getData();
    } catch (error) {
      console.error("Auto-save failed:", error);
      toast.error("Auto-lagring feilet. Prøv igjen.", {
        position: "top-right",
      });
    }
  };

  const fileEntreprenørgarantiRef = React.useRef<HTMLInputElement | null>(null);

  const handleEntreprenørgarantiClick = () => {
    fileEntreprenørgarantiRef.current?.click();
  };
  const handleEntreprenørgarantiDragOver = (
    event: React.DragEvent<HTMLDivElement>
  ) => {
    event.preventDefault();
  };

  const handleDocumentUpload = async (files: FileList, fieldName: any) => {
    if (!files.length) return;

    let newImages = [...(form.watch(fieldName) || [])];

    const uploadPromises = Array.from(files).map(async (file) => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size must be less than 5MB.", {
          position: "top-right",
        });
        return null;
      }

      const fileType = "documents";
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = reff(storage, `${fileType}/${fileName}`);

      try {
        const snapshot = await uploadBytes(storageRef, file);
        return await getDownloadURL(snapshot.ref);
      } catch (error) {
        console.error("Error uploading file:", error);
        return null;
      }
    });

    const uploadedUrls = (await Promise.all(uploadPromises)).filter(
      Boolean
    ) as string[];

    if (uploadedUrls.length) {
      newImages = [...newImages, ...uploadedUrls];
      form.setValue(fieldName, newImages);
      form.clearErrors(fieldName);
      await autoSave();
    }
  };
  const handleEntreprenørgarantiDrop = async (
    event: React.DragEvent<HTMLDivElement>
  ) => {
    event.preventDefault();
    if (event.dataTransfer.files) {
      await handleDocumentUpload(
        event.dataTransfer.files,
        "Entreprenørgaranti"
      );
    }
  };

  const fileForsikringsbevisRef = React.useRef<HTMLInputElement | null>(null);

  const handleForsikringsbevisClick = () => {
    fileForsikringsbevisRef.current?.click();
  };
  const handleForsikringsbevisDragOver = (
    event: React.DragEvent<HTMLDivElement>
  ) => {
    event.preventDefault();
  };
  const handleForsikringsbevisDrop = async (
    event: React.DragEvent<HTMLDivElement>
  ) => {
    event.preventDefault();
    if (event.dataTransfer.files) {
      await handleDocumentUpload(event.dataTransfer.files, "Forsikringsbevis");
    }
  };
  const handleForsikringsbevisFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files) {
      await handleDocumentUpload(event.target.files, "Forsikringsbevis");
    }
  };

  const fileKontraktRef = React.useRef<HTMLInputElement | null>(null);

  const handleKontraktClick = () => {
    fileKontraktRef.current?.click();
  };
  const handleKontraktDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };
  const handleKontraktDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (event.dataTransfer.files) {
      await handleDocumentUpload(event.dataTransfer.files, "Kontrakt");
    }
  };
  const handleKontraktFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files) {
      await handleDocumentUpload(event.target.files, "Kontrakt");
    }
  };

  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [deleteField, setDeleteField] = useState<string | null>(null);

  const handleDeleteClick = (index: number) => {
    setDeleteIndex(index);
    setShowConfirm(true);
  };

  const handleEntreprenørgarantiFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files) {
      await handleDocumentUpload(event.target.files, "Entreprenørgaranti");
    }
  };

  const handleCancelDelete = () => {
    setShowConfirm(false);
    setDeleteField(null);
    setDeleteIndex(null);
  };

  const handleConfirmPopup = () => {
    if (showConfirm) {
      setShowConfirm(false);
      setDeleteField(null);
    } else {
      setShowConfirm(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (deleteIndex !== null && deleteField) {
      const updatedFiles = form
        .watch(deleteField)
        .filter((_: any, i: number) => i !== deleteIndex);
      form.setValue(deleteField, updatedFiles);
      await autoSave();
    }
    setShowConfirm(false);
    setDeleteIndex(null);
    setDeleteField(null);
  };

  const handleDownload = async (filePath: string) => {
    try {
      if (!filePath) {
        console.error("File path is missing!");
        return;
      }

      const storage = getStorage();
      const fileRef = ref(storage, filePath);
      const url = await getDownloadURL(fileRef);

      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.download = filePath.split("/").pop() || "download";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="relative">
          <div className="mb-20">
            <div className="flex flex-col gap-4 md:gap-6">
              <div className="rounded-lg bg-lightGreen w-full">
                <div className="p-3 md:p-4">
                  <h3 className="text-base lg:text-lg font-semibold text-black3">
                    Entreprenørgaranti
                  </h3>
                </div>
                <div className="p-1 pt-0">
                  <div className="bg-white p-4 rounded-lg border border-gray3 shadow-shadow4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      <FormField
                        control={form.control}
                        name="Entreprenørgaranti"
                        render={() => (
                          <FormItem className="w-full">
                            <FormControl>
                              <div className="relative w-full p-2 rounded-lg bg-skyBlue">
                                <div className="bg-white p-1">
                                  <div
                                    className="flex gap-4 items-center p-2 justify-center border-gray3 border rounded-lg shadow-shadow5 h-[120px]"
                                    onDragOver={
                                      handleEntreprenørgarantiDragOver
                                    }
                                    onClick={handleEntreprenørgarantiClick}
                                    onDrop={handleEntreprenørgarantiDrop}
                                  >
                                    <div className="bg-primary h-12 w-12 justify-center items-center flex rounded-full">
                                      <Upload className="text-white" />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                      <div className="text-black3 font-semibold text-xs md:text-sm">
                                        Last opp dokumenter
                                      </div>
                                      <p className="text-gray4 text-xs md:text-sm">
                                        Slipp fil for å laste opp her
                                      </p>
                                    </div>

                                    <input
                                      type="file"
                                      ref={fileEntreprenørgarantiRef}
                                      className="hidden"
                                      accept=".pdf"
                                      onChange={
                                        handleEntreprenørgarantiFileChange
                                      }
                                      name="Entreprenørgaranti"
                                      multiple
                                    />
                                  </div>
                                </div>
                                <p className="text-gray4 text-xs md:text-sm text-center w-full mt-3">
                                  Filformater: kun{" "}
                                  <span className="text-black3">
                                    PDF, max 5 MB
                                  </span>
                                </p>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <div>
                        <p className="text-black3 mb-3 py-2 text-sm md:text-base font-semibold">
                          Dokumenter
                        </p>
                        {Entreprenørgaranti && Entreprenørgaranti.length > 0 ? (
                          <div className="flex flex-col items-center gap-3">
                            {Entreprenørgaranti?.map(
                              (file: any, index: number) => (
                                <div
                                  className="border border-gray3 rounded-lg p-2 md:p-3 flex items-center justify-between relative w-full"
                                  key={index}
                                >
                                  <div className="flex items-start gap-2 md:gap-4 truncate">
                                    <div className="bg-lightGreen w-10 h-10 rounded-full flex justify-center items-center">
                                      <img src={Ic_file} alt="file" />
                                    </div>
                                    <FileInfo file={file} />
                                  </div>
                                  <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
                                    <div
                                      className="cursor-pointer"
                                      onClick={() => {
                                        handleDeleteClick(index);
                                        setDeleteField("Entreprenørgaranti");
                                      }}
                                    >
                                      <img src={Ic_trash} alt="delete" />
                                    </div>
                                    <div
                                      className="cursor-pointer"
                                      onClick={() => handleDownload(file)}
                                    >
                                      <img
                                        src={Ic_download_primary}
                                        alt="download"
                                      />
                                    </div>
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        ) : (
                          <p className="text-gray">
                            Mangler entreprenørgaranti
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-lg bg-lightGreen w-full">
                <div className="p-3 md:p-4">
                  <h3 className="text-base lg:text-lg font-semibold text-black3">
                    Forsikringsbevis
                  </h3>
                </div>
                <div className="p-1 pt-0">
                  <div className="bg-white p-4 rounded-lg border border-gray3 shadow-shadow4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      <FormField
                        control={form.control}
                        name="Forsikringsbevis"
                        render={() => (
                          <FormItem className="w-full">
                            <FormControl>
                              <div className="relative w-full p-2 rounded-lg bg-skyBlue">
                                <div className="bg-white p-1">
                                  <div
                                    className="flex gap-4 items-center p-2 justify-center border-gray3 border rounded-lg shadow-shadow5 h-[120px]"
                                    onDragOver={handleForsikringsbevisDragOver}
                                    onClick={handleForsikringsbevisClick}
                                    onDrop={handleForsikringsbevisDrop}
                                  >
                                    <div className="bg-primary h-12 w-12 justify-center items-center flex rounded-full">
                                      <Upload className="text-white" />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                      <div className="text-black3 font-semibold text-xs md:text-sm">
                                        Last opp dokumenter
                                      </div>
                                      <p className="text-gray4 text-xs md:text-sm">
                                        Slipp fil for å laste opp her
                                      </p>
                                    </div>

                                    <input
                                      type="file"
                                      ref={fileForsikringsbevisRef}
                                      className="hidden"
                                      accept=".pdf"
                                      onChange={
                                        handleForsikringsbevisFileChange
                                      }
                                      name="Forsikringsbevis"
                                      multiple
                                    />
                                  </div>
                                </div>
                                <p className="text-gray4 text-xs md:text-sm text-center w-full mt-3">
                                  Filformater: kun{" "}
                                  <span className="text-black3">
                                    PDF, max 5 MB
                                  </span>
                                </p>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <div>
                        <p className="text-black3 mb-3 py-2 text-sm md:text-base font-semibold">
                          Dokumenter
                        </p>
                        {Forsikringsbevis && Forsikringsbevis.length > 0 ? (
                          <div className="flex flex-col items-center gap-3">
                            {Forsikringsbevis?.map(
                              (file: any, index: number) => (
                                <div
                                  className="border border-gray3 rounded-lg p-2 md:p-3 flex items-center justify-between relative w-full"
                                  key={index}
                                >
                                  <div className="flex items-start gap-2 md:gap-4 truncate">
                                    <div className="bg-lightGreen w-10 h-10 rounded-full flex justify-center items-center">
                                      <img src={Ic_file} alt="file" />
                                    </div>
                                    <FileInfo file={file} />
                                  </div>
                                  <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
                                    <div
                                      className="cursor-pointer"
                                      onClick={() => {
                                        handleDeleteClick(index);
                                        setDeleteField("Forsikringsbevis");
                                      }}
                                    >
                                      <img src={Ic_trash} alt="delete" />
                                    </div>
                                    <div
                                      className="cursor-pointer"
                                      onClick={() => handleDownload(file)}
                                    >
                                      <img
                                        src={Ic_download_primary}
                                        alt="download"
                                      />
                                    </div>
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        ) : (
                          <p className="text-gray">Mangler forsikringsbevis</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-lg bg-lightGreen w-full">
                <div className="p-3 md:p-4">
                  <h3 className="text-base lg:text-lg font-semibold text-black3">
                    Kontrakt <span className="text-grey">(eks. NS-3425)</span>
                  </h3>
                </div>
                <div className="p-1 pt-0">
                  <div className="bg-white p-4 rounded-lg border border-gray3 shadow-shadow4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      <FormField
                        control={form.control}
                        name="Kontrakt"
                        render={() => (
                          <FormItem className="w-full">
                            <FormControl>
                              <div className="relative w-full p-2 rounded-lg bg-skyBlue">
                                <div className="bg-white p-1">
                                  <div
                                    className="flex gap-4 items-center p-2 justify-center border-gray3 border rounded-lg shadow-shadow5 h-[120px]"
                                    onDragOver={handleKontraktDragOver}
                                    onClick={handleKontraktClick}
                                    onDrop={handleKontraktDrop}
                                  >
                                    <div className="bg-primary h-12 w-12 justify-center items-center flex rounded-full">
                                      <Upload className="text-white" />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                      <div className="text-black3 font-semibold text-xs md:text-sm">
                                        Last opp dokumenter
                                      </div>
                                      <p className="text-gray4 text-xs md:text-sm">
                                        Slipp fil for å laste opp her
                                      </p>
                                    </div>

                                    <input
                                      type="file"
                                      ref={fileKontraktRef}
                                      className="hidden"
                                      accept=".pdf"
                                      onChange={handleKontraktFileChange}
                                      name="Kontrakt"
                                      multiple
                                    />
                                  </div>
                                </div>
                                <p className="text-gray4 text-xs md:text-sm text-center w-full mt-3">
                                  Filformater: kun{" "}
                                  <span className="text-black3">
                                    PDF, max 5 MB
                                  </span>
                                </p>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <div>
                        <p className="text-black3 mb-3 py-2 text-sm md:text-base font-semibold">
                          Dokumenter
                        </p>
                        {Kontrakt && Kontrakt.length > 0 ? (
                          <div className="flex flex-col items-center gap-3">
                            {Kontrakt?.map((file: any, index: number) => (
                              <div
                                className="border border-gray3 rounded-lg p-2 md:p-3 flex items-center justify-between relative w-full"
                                key={index}
                              >
                                <div className="flex items-start gap-2 md:gap-4 truncate">
                                  <div className="bg-lightGreen w-10 h-10 rounded-full flex justify-center items-center">
                                    <img src={Ic_file} alt="file" />
                                  </div>
                                  <FileInfo file={file} />
                                </div>
                                <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
                                  <div
                                    className="cursor-pointer"
                                    onClick={() => {
                                      handleDeleteClick(index);
                                      setDeleteField("Kontrakt");
                                    }}
                                  >
                                    <img src={Ic_trash} alt="delete" />
                                  </div>
                                  <div
                                    className="cursor-pointer"
                                    onClick={() => handleDownload(file)}
                                  >
                                    <img
                                      src={Ic_download_primary}
                                      alt="download"
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray">Mangler kontrakt</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white pb-2 bottom-0 fixed w-[calc(100%-16px)] left-2">
            <div className="flex justify-end w-full gap-4 items-center  bg-white z-50 border border-gray3 p-3 md:p-4">
              <Button
                text="Avbryt"
                className="border-2 border-primary text-primary text-base rounded-[34px] h-10 md:h-12 font-semibold relative px-6 md:px-8 py-[10px] flex items-center gap-2"
                onClick={() => setActiveTab(1)}
              />
              <div id="submit">
                <Button
                  text="Neste"
                  className="border border-primary bg-primary text-white text-base rounded-[34px] h-10 md:h-12 font-semibold relative px-6 md:px-8 py-[10px] flex items-center gap-2"
                  type="submit"
                />
              </div>
            </div>
          </div>
        </form>
      </Form>

      {showConfirm && (
        <Modal onClose={handleConfirmPopup} isOpen={true}>
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
            <div className="bg-white p-6 rounded-lg">
              <p className="mb-4">Er du sikker på at du vil slette?</p>
              <div className="flex justify-center gap-4">
                <div onClick={handleCancelDelete}>
                  <Button
                    text="Avbryt"
                    className="border border-gray2 text-black text-sm rounded-[8px] h-[40px] font-medium relative px-4 py-[10px] flex items-center gap-2"
                  />
                </div>
                <Button
                  text="Bekrefte"
                  className="border border-primary bg-primary text-white text-sm rounded-[8px] h-[40px] font-medium relative px-4 py-[10px] flex items-center gap-2"
                  onClick={handleConfirmDelete}
                />
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};
